import React, { useState, useMemo, useCallback } from 'react';
import useTranslate from '../../../../../../../Hooks/useTranslate';
import {
  Box,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Divider,
  useTheme,
  Button,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { Virtuoso } from 'react-virtuoso';
import { CenteredFlexBox } from '../../../../../../../Components/FlexBox';
import Chip from '../../../../../../../Components/Chip';
import Select from '../../../../../../../Components/Inputs/Select';
import CustomerPaper from './CustomerPaper';

// Hard cap on the open accordion's content height. The customer list never
// grows past this; beyond it the list scrolls internally.
const MAX_CONTENT_HEIGHT = '45vh';

const OverlapAccordion = function OverlapAccordion({
  simulationId,
  customerCount,
  sectionCombination,
  overlapDetails = [],
  sections = [],
  submitResolvedOverlaps = () => {},
}) {
  const theme = useTheme();
  const translate = useTranslate();
  const [open, setOpen] = useState(false);
  const [moveAllValue, setMoveAllValue] = useState('');
  // Single source of truth for this accordion: { [customerId]: winnerCustomerWavesId }.
  // Lifting it here lets "move all" update every customer in one batched setState
  // instead of one cascading state update per CustomerPaper.
  const [resolved, setResolved] = useState({});

  const sectionNameById = useMemo(() => {
    const map = {};
    sections.forEach(({ id, name }) => {
      map[id?.toString()] = name;
    });
    return map;
  }, [sections]);

  const options = useMemo(
    () =>
      sections
        ?.filter(({ id }) => sectionCombination?.includes(id))
        ?.map(({ id, name }) => ({ value: id, label: name })),
    [sections, sectionCombination]
  );

  const handleMoveAll = useCallback(
    (newValue) => {
      setMoveAllValue(newValue);
      setResolved(() => {
        if (!newValue) return {};
        const next = {};
        overlapDetails.forEach(({ customerId }) => {
          next[customerId] = newValue;
        });
        return next;
      });
    },
    [overlapDetails]
  );

  const handleCustomerChange = useCallback(
    (customerId, winnerCustomerWavesId) => {
      setResolved((prev) => {
        const next = { ...prev };
        if (!winnerCustomerWavesId) {
          delete next[customerId];
        } else {
          next[customerId] = winnerCustomerWavesId;
        }
        return next;
      });
    },
    []
  );

  const resolvedCount = useMemo(
    () => Object.values(resolved).filter(Boolean).length,
    [resolved]
  );

  const handleConfirmResolveOverlaps = () => {
    const payload = overlapDetails
      .filter(({ customerId }) => resolved[customerId])
      .map(({ customerId }) => ({
        customerId,
        winnerCustomerWavesId: resolved[customerId],
      }));
    submitResolvedOverlaps(simulationId, payload);
  };

  return (
    <Accordion
      expanded={open}
      onChange={(event, expanded) => setOpen(expanded)}
      slotProps={{ transition: { unmountOnExit: true } }}
    >
      <AccordionSummary expandIcon={<ExpandMoreIcon />}>
        <CenteredFlexBox gap="16px" width="100%" padding="8px">
          <CenteredFlexBox gap="16px" flexGrow={1}>
            <Typography variant="h6">{translate('SECTIONS')}:</Typography>
            {sectionCombination.split(',')?.map((section) => (
              <Chip
                key={section}
                id={section}
                variant="outlined"
                fontSize="1rem"
                label={sectionNameById[section]}
              />
            ))}
            <Divider orientation="vertical" flexItem />
            <Typography variant="h6">
              {translate('CUSTOMERS')}:{' '}
              <span
                style={{
                  fontWeight: 'bold',
                  color: theme.palette.primary.main,
                }}
              >
                {customerCount}
              </span>
            </Typography>
          </CenteredFlexBox>
          <Typography variant="h6">
            {translate('MOVE_ALL_TO_SECTION')}
          </Typography>
          <Select
            name="section-select"
            size="small"
            options={options}
            value={moveAllValue}
            onChange={handleMoveAll}
            width="240px"
            nullable
          />
        </CenteredFlexBox>
      </AccordionSummary>
      <AccordionDetails sx={{ padding: 0 }}>
        {overlapDetails.length > 0 && (
          <Virtuoso
            style={{
              height: MAX_CONTENT_HEIGHT,
            }}
            data={overlapDetails}
            computeItemKey={(index, detail) => detail.customerId}
            itemContent={(
              index,
              { customerId, fullName, previousSectionId, branches }
            ) => (
              <Box
                sx={{
                  paddingX: '16px',
                  paddingTop: index === 0 ? '16px' : 0,
                  paddingBottom: '8px',
                }}
              >
                <CustomerPaper
                  customerId={customerId}
                  previousSection={
                    sectionNameById[previousSectionId?.toString()]
                  }
                  fullName={fullName}
                  branches={branches}
                  value={resolved[customerId] ?? ''}
                  onChange={handleCustomerChange}
                  options={options}
                />
              </Box>
            )}
          />
        )}
        <CenteredFlexBox
          padding="8px"
          gap="16px"
          justifyContent="flex-end"
          sx={{
            borderTop: `1px solid ${theme.palette.divider}`,
            backgroundColor: 'background.paper',
          }}
        >
          <Button
            id={`confirm-overlaps-${sectionCombination}`}
            variant="contained"
            onClick={handleConfirmResolveOverlaps}
            disabled={
              resolvedCount === 0 || resolvedCount < overlapDetails.length
            }
          >
            {translate('RESOLVE_OVERLAPS')}
          </Button>
        </CenteredFlexBox>
      </AccordionDetails>
    </Accordion>
  );
};

export default OverlapAccordion;
